import React from 'react';
import { SmartTransactionInbox } from '@/components/escrow/SmartTransactionInbox';
// import { BalanceCard } from '@/components/wallet/BalanceCard'; // Component not yet implemented
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@vaiu/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@vaiu/ui/alert';
import { Info, Shield, Zap, TrendingUp } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

export default function SmartTransactionsPage() {
  const { user } = useAuth();
  
  // Determine user role based on their profile
  const getUserRole = () => {
    if (user?.otherProfiles?.merchant && user?.otherProfiles?.buyer) {
      return 'both';
    } else if (user?.otherProfiles?.merchant) {
      return 'seller';
    } else {
      return 'buyer';
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Smart Transactions</h1>
        <p className="text-muted-foreground">
          Manage transactions that require verification before completion
        </p>
      </div>

      {/* Educational Alert */}
      <Alert className="mb-6">
        <Shield className="h-4 w-4" />
        <AlertTitle>Protected Transactions</AlertTitle>
        <AlertDescription>
          Smart Transactions use escrow to protect both buyers and sellers. Funds are held securely 
          until verification is complete - either manually by the buyer or automatically through 
          third-party services.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Smart Transaction Inbox */}
        <div className="lg:col-span-2">
          <SmartTransactionInbox userRole={getUserRole()} />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Balance Card */}
          {/* <BalanceCard /> */}
          {/* Balance card component to be implemented */}

          {/* Verification Methods */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Verification Methods</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Info className="w-4 h-4 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Manual Verification</p>
                    <p className="text-xs text-muted-foreground">
                      Buyer confirms receipt of goods/services
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Automated Delivery</p>
                    <p className="text-xs text-muted-foreground">
                      FedEx/UPS confirms package delivery
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Identity Verification</p>
                    <p className="text-xs text-muted-foreground">
                      KYC providers verify identity claims
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Smart Contracts</p>
                    <p className="text-xs text-muted-foreground">
                      Condition-based automatic release
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Protection Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Avg. Verification Time</span>
                <span className="font-medium">2.5 days</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Auto-Release After</span>
                <span className="font-medium">14 days</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Dispute Rate</span>
                  <span className="font-medium text-green-600">&lt; 1%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Success Rate</span>
                <span className="font-medium text-green-600">99.7%</span>
              </div>
            </CardContent>
          </Card>

          {/* Help Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Need Help?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Learn more about Smart Transactions and how they protect your payments.
              </p>
              <div className="space-y-2">
                <a href="/help/smart-transactions" className="text-sm text-primary hover:underline block">
                  → How Smart Transactions Work
                </a>
                <a href="/help/verification" className="text-sm text-primary hover:underline block">
                  → Verification Methods
                </a>
                <a href="/help/disputes" className="text-sm text-primary hover:underline block">
                  → Dispute Resolution
                </a>
                <a href="/support" className="text-sm text-primary hover:underline block">
                  → Contact Support
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
